import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["Component", "use", "children", "style", "primaryColor"];
import { useState, useCallback } from 'react';
import { rgba } from 'foundations-components/transitional/utils/color';
import { useTheme } from 'foundations-theming/utils';
import { canReadText, getTextColorWithTheme, getWhiteness } from '../../utils/colors';
import { jsx as _jsx } from "react/jsx-runtime";
const PRIMARY_COLOR_USE = ['primary-color', 'primary-color-secondary', 'primary-color-link'];
/**
 * Makes a readable button given different background colors
 * @deprecated See description of https://git.hubteam.com/HubSpotProtected/payments-ui/pull/350
 * For context, ticket to replace at https://git.hubteam.com/HubSpot/Buyer-Exp-Issues/issues/511
 */
const CustomColorButton = _ref => {
  let {
      Component,
      use,
      children,
      style,
      primaryColor: primaryColorProp
    } = _ref,
    transferrableProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const primaryColor = primaryColorProp || theme.color['fill-accent-neutral-default'];
  const [hovered, setHovered] = useState(false);
  const getButtonUse = useCallback(() => {
    if (use && !PRIMARY_COLOR_USE.includes(use)) {
      return use;
    }
    if (use === 'primary-color-link') {
      return 'link';
    }

    // Return a use override so the spinner renders correctly when using
    // UILoadingButton
    return getTextColorWithTheme(primaryColor, theme) === theme.color['text-primary-default'] ? 'secondary-ghost' : 'primary-white';
  }, [use, primaryColor, theme]);
  const getButtonStyle = useCallback(() => {
    if (use && !PRIMARY_COLOR_USE.includes(use)) {
      return style;
    }
    if (getWhiteness(primaryColor)) {
      const backgroundColor = hovered ? rgba(primaryColor, 0.8) : primaryColor;
      return Object.assign({}, style, {
        borderColor: theme.color['border-core-default'],
        backgroundColor
      });
    }
    if (use === 'primary-color-link') {
      return canReadText(theme.color['text-primary-default'], theme, primaryColor) ? Object.assign({}, style, {
        color: primaryColor
      }) : style;
    }
    if (use === 'primary-color-secondary') {
      if (!canReadText(primaryColor, theme)) {
        return Object.assign({}, style, {
          color: theme.color['text-core-default'],
          borderColor: theme.color['text-core-default']
        });
      }
      const backgroundColor = hovered ? rgba(primaryColor, 0.05) : theme.color['text-primary-default'];
      return Object.assign({}, style, {
        color: primaryColor,
        borderColor: primaryColor,
        backgroundColor
      });
    }
    const backgroundColor = hovered ? rgba(primaryColor, 0.8) : primaryColor;
    return Object.assign({}, style, {
      color: getTextColorWithTheme(primaryColor, theme),
      borderColor: primaryColor,
      backgroundColor
    });
  }, [hovered, primaryColor, style, use, theme]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(Component, Object.assign({}, transferrableProps, {
      role: "button"
      // @bend-expect trellis-tokenized
      ,
      use: getButtonUse(),
      style: getButtonStyle(),
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      children: children
    }))
  );
};
export default CustomColorButton;